import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  Link,
} from '@mui/material';
import { CloudUpload as UploadIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import AuthService from '../services/AuthService';

/**
 * ZIP/XML Parser Component
 * Processes ZIP files containing XML documents from JTL's DUO system
 * Extracts XMLs, modifies customer numbers to ensure 7-digit format,
 * and repackages them back into ZIP format
 */
const ZipXmlParser: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [tabIndex, setTabIndex] = useState(4); // For ZIP XML Parser tab - moved to the end
  
  // Reference to file input for resetting
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch file list on component mount and after operations
  useEffect(() => {
    fetchFiles();
  }, []);
  
  // Show success message effect
  useEffect(() => {
    if (parseSuccess) {
      // Save tab index in local storage before refresh
      localStorage.setItem('activeTabIndex', tabIndex.toString());
      localStorage.setItem('showParseSuccess', 'true');
      
      // Refresh the page after a short delay
      const timer = setTimeout(() => {
        window.location.reload();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [parseSuccess, tabIndex]);

  // Check for success message after page reload
  useEffect(() => {
    const savedTabIndex = localStorage.getItem('activeTabIndex');
    const showSuccess = localStorage.getItem('showParseSuccess');
    
    if (showSuccess === 'true') {
      setSuccess('Dateien erfolgreich verarbeitet und gespeichert');
      
      // Clear the flag
      localStorage.removeItem('showParseSuccess');
      
      // Auto-hide the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const fetchFiles = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/zip/files`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Failed to fetch files (Status: ${response.status})`);
      }

      const data = await response.json();
      setFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError((err as Error).message);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    
    // Append all selected files to the FormData
    for (let i = 0; i < files.length; i++) {
      formData.append("zip", files[i]);
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus("Dateien werden hochgeladen...");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/zip/upload`, {
        method: "POST",
        body: formData,
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      resetFileInput();

      if (!response.ok) {
        throw new Error(`Fehler beim Hochladen der Datei(en) (Status: ${response.status})`);
      }

      const result = await response.json();
      setSuccess("Dateien erfolgreich hochgeladen");
      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error in file upload:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  const processFiles = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus("Dateien werden verarbeitet...");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/zip/process`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        throw new Error(`Fehler beim Verarbeiten der Dateien (Status: ${response.status})`);
      }

      const result = await response.json();
      setSuccess(`${result.processed} Dateien erfolgreich verarbeitet`);
      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error processing files:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  // Function to delete a file from the server
  const deleteFile = async (filename: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/zip/delete/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload();
          return;
        }
        throw new Error(`Fehler beim Löschen der Datei (Status: ${response.status})`);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      // Don't show error to user, just log it
    }
  };

  const downloadFile = async (filename: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/zip/download/${encodeURIComponent(filename)}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Fehler beim Herunterladen der Datei (Status: ${response.status})`);
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Get the base filename to find related files to delete
      const baseFilename = filename.replace('_parsed.zip', '');
      
      // Find the original file based on the related base name
      const originalFile = files.find(f => 
        f.endsWith('.zip') && 
        !f.includes('_parsed') && 
        f.includes(baseFilename)
      );
      
      // Delete both the original and parsed files
      if (originalFile) {
        await deleteFile(originalFile);
      }
      await deleteFile(filename);
      
      // Set parse success flag to trigger page refresh
      setParseSuccess(true);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError((err as Error).message);
    }
  };

  // Function to reset the file input
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        ZIP (XML) Parser
      </Typography>
      
      <Typography variant="body1" paragraph>
        Die hochgeladenen ZIP (XML) Dateien werden geparst und alle Kundennummern,
        welche &gt; 1000000 (7 stellen) werden belassen, die anderen KD Nummern
        werden dagegen mit einem 00-Suffix geändert, damit alle KD Nummern 7-Stellen haben.
      </Typography>
      
      <Typography variant="body2" paragraph>
        Hochgeladen werden muss:
        <ul>
          <li>Verkauf: Alle mit DUO erzeugten zip Dateien z.B. "VK_Belege2021-12_1.zip"</li>
        </ul>
      </Typography>
      
      {processingStatus && (
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>{processingStatus}</Typography>
        </Box>
      )}

      <Box mb={4} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={2} alignItems="center">
          <input 
            ref={fileInputRef}
            accept=".zip" 
            style={{ display: "none" }} 
            id="zip-file-upload" 
            type="file" 
            multiple
            onChange={handleFileUpload} 
            disabled={loading} 
          />
          <label htmlFor="zip-file-upload">
            <Button 
              variant="contained" 
              component="span" 
              disabled={loading}
              startIcon={<UploadIcon />}
            >
              1. ZIP(s) hochladen
            </Button>
          </label>
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={processFiles}
            disabled={loading || files.length === 0}
          >
            2. Verarbeiten
          </Button>
        </Box>
      </Box>
      
      {files.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>
            3. Verarbeitete Dateien herunterladen:
          </Typography>
          
          <List>
            {files.map((file, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                {file.includes('_parsed') ? (
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={() => downloadFile(file)}
                  >
                    {file}
                  </Link>
                ) : (
                  <Typography variant="body2">{file}</Typography>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ZipXmlParser;